/**
 * Wave E (decision 0063, spec §7.4, §7.5): machines by owner, in one set of words for the runners page, the workspace
 * bar's pill and the Team tab: "Yours", "Becky's", "Shared with the team", "Just you", "Just Becky's". Whether the viewer
 * may use a machine is core's `mayRun` (the web never re-implements it). Pure, so every rule is unit-tested.
 */
import type { RunnerFactsOf } from "@minga/core"
import { mayRun } from "@minga/core"
import type { HomeData, RunnerLiveEvent, RunnerLogins, RunnerPublicView, RunnerServes } from "@minga/protocol"

/** The fields every runner list the web holds has (H8 rows, v1 list items, the live push's public view). */
export interface RunnerLike {
  readonly runnerId: string
  readonly name: string
  readonly ownerUserId: string
  readonly status: "online" | "offline"
  readonly serves?: RunnerServes | undefined
}

/** Just me / The team; absent (a row from before E) = Just me. */
export const servesOf = (r: object): RunnerServes =>
  (r as { readonly serves?: RunnerServes | undefined }).serves ?? "owner"

/** Core's `mayRun` for "work the viewer asked for" on this runner (no lend: a lend covers one item only). */
export const mayUse = (r: RunnerLike, viewerId: string): boolean =>
  mayRun(
    { id: r.runnerId as never, ownerId: r.ownerUserId as never, serves: servesOf(r) },
    {
      ownerId: viewerId as never,
    },
  )

/** Core's `RunnerFactsOf` (whose machine, Just me or The team) over any runner list. */
export const runnerFactsOf =
  (rows: ReadonlyArray<RunnerLike>): RunnerFactsOf =>
  (runnerId) => {
    const r = rows.find((x) => x.runnerId === runnerId)
    return r ? { ownerId: r.ownerUserId as never, serves: servesOf(r) } : undefined
  }

/** "Bob's" (names ending in s keep the plain 's, as the rest of the app writes them). */
export const possessive = (name: string): string => `${name}'s`

/** The chip on every card: "Shared with the team", "Just you", "Just Becky's". */
export const servesChip = (r: RunnerLike, viewerId: string, ownerName: string | null): string =>
  servesOf(r) === "team"
    ? "Shared with the team"
    : r.ownerUserId === viewerId
      ? "Just you"
      : `Just ${ownerName ? possessive(ownerName) : "its owner's"}`

export interface OwnerGroup<R extends RunnerLike> {
  /** `yours` or `user:<id>`. */
  readonly key: string
  /** "Yours", "Jannik's". */
  readonly label: string
  readonly ownerId: string
  readonly runners: ReadonlyArray<R>
}

/**
 * The runners page: one group per owner. Yours first (always, empty when you have paired none), then the people who
 * share a machine with the team (the machines you can use), then the rest, each by name; online machines first in each
 * group.
 */
export const groupByOwner = <R extends RunnerLike>(
  rows: ReadonlyArray<R>,
  viewerId: string,
  names: ReadonlyMap<string, string>,
): ReadonlyArray<OwnerGroup<R>> => {
  const groups = groupByOwnerOnly(rows, viewerId, names)
  return groups.some((g) => g.ownerId === viewerId) || viewerId === ""
    ? groups
    : [{ key: "yours", label: "Yours", ownerId: viewerId, runners: [] }, ...groups]
}

/** "You haven't paired a machine. Your work runs on a team machine." (the empty Yours group). */
export const noMachineLine = (rows: ReadonlyArray<RunnerLike>, viewerId: string): string =>
  rows.some((r) => r.ownerUserId !== viewerId && servesOf(r) === "team")
    ? "You haven't paired a machine. Your work runs on a team machine."
    : "You haven't paired a machine yet."

const groupByOwnerOnly = <R extends RunnerLike>(
  rows: ReadonlyArray<R>,
  viewerId: string,
  names: ReadonlyMap<string, string>,
): ReadonlyArray<OwnerGroup<R>> => {
  const byOwner = new Map<string, R[]>()
  for (const r of rows) byOwner.set(r.ownerUserId, [...(byOwner.get(r.ownerUserId) ?? []), r])
  const order = (list: ReadonlyArray<R>) =>
    [...list].sort(
      (a, b) => Number(b.status === "online") - Number(a.status === "online") || a.name.localeCompare(b.name),
    )
  const label = (ownerId: string) =>
    ownerId === viewerId ? "Yours" : possessive(names.get(ownerId) ?? "A teammate")
  return [...byOwner.entries()]
    .map(([ownerId, list]) => ({
      key: ownerId === viewerId ? "yours" : `user:${ownerId}`,
      label: label(ownerId),
      ownerId,
      runners: order(list),
    }))
    .sort((a, b) => {
      const shares = (g: { readonly runners: ReadonlyArray<R> }) =>
        g.runners.some((r) => servesOf(r) === "team")
      return (
        Number(b.ownerId === viewerId) - Number(a.ownerId === viewerId) ||
        Number(shares(b)) - Number(shares(a)) ||
        a.label.localeCompare(b.label)
      )
    })
}

/** "3 machines · 3 live · you can use 2" (the runners page summary). */
export const summaryLine = (rows: ReadonlyArray<RunnerLike>, viewerId: string): string => {
  const live = rows.filter((r) => r.status === "online").length
  const usable = rows.filter((r) => mayUse(r, viewerId)).length
  const machines = rows.length === 1 ? "1 machine" : `${rows.length} machines`
  return `${machines} · ${live} live · you can use ${usable}`
}

export interface PillGroups<R extends RunnerLike> {
  /** The viewer's own machines (shared or not). */
  readonly yours: ReadonlyArray<R>
  /** Other people's machines set to The team. */
  readonly team: ReadonlyArray<R>
  /** Other people's personal machines, one row per person. */
  readonly others: ReadonlyArray<{
    readonly ownerId: string
    readonly name: string
    readonly runners: ReadonlyArray<R>
  }>
}

/** The pill's popover: Yours · Shared with the team · Others' personal (one row per person). */
export const pillGroups = <R extends RunnerLike>(
  rows: ReadonlyArray<R>,
  viewerId: string,
  names: ReadonlyMap<string, string>,
): PillGroups<R> => {
  const yours = rows.filter((r) => r.ownerUserId === viewerId)
  const team = rows.filter((r) => r.ownerUserId !== viewerId && servesOf(r) === "team")
  const personal = rows.filter((r) => r.ownerUserId !== viewerId && servesOf(r) !== "team")
  const owners = [...new Set(personal.map((r) => r.ownerUserId))]
  return {
    yours,
    team,
    others: owners
      .map((ownerId) => ({
        ownerId,
        name: names.get(ownerId) ?? "A teammate",
        runners: personal.filter((r) => r.ownerUserId === ownerId),
      }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  }
}

export interface PillState {
  /** "3 live". */
  readonly label: string
  /** green dot: a machine you can use is live; hollow grey: none; amber triangle: one of yours needs attention. */
  readonly tone: "live" | "none" | "attention"
  /** "3 machines live, 2 you can use". */
  readonly aria: string
}

/** The workspace bar's pill (spec §7.4). `attention(r)` says whether one of your machines needs you. */
export const pillState = <R extends RunnerLike>(
  rows: ReadonlyArray<R>,
  viewerId: string,
  attention: (r: R) => boolean = () => false,
): PillState => {
  const live = rows.filter((r) => r.status === "online")
  const usableLive = live.filter((r) => mayUse(r, viewerId)).length
  const yoursNeed = rows.some((r) => r.ownerUserId === viewerId && attention(r))
  return {
    // decision 0159 (jargon row 34): never "0 live"
    label: live.length === 0 ? "No machines online" : `${live.length} live`,
    tone: yoursNeed ? "attention" : usableLive > 0 ? "live" : "none",
    aria: `${live.length} ${live.length === 1 ? "machine" : "machines"} live, ${usableLive} you can use`,
  }
}

const cliWords = (cli: "Claude" | "Codex", login: RunnerLogins["claude"]): string | null => {
  if (!login) return null
  if (login.kind === "api_key") return `${cli} (API key)`
  return [cli, login.plan].filter(Boolean).join(" ")
}

/**
 * The logins line (spec §3.5): "GitHub @dictly-bot · Claude (API key) · Codex Plus". Null when there is nothing to
 * say. An older runner (`known: false`) adds that the line may be incomplete.
 */
export const loginsLine = (logins: RunnerLogins | null): string | null => {
  if (!logins) return null
  const gh = logins.github
    ? `GitHub @${logins.github.login}${logins.github.host && logins.github.host !== "github.com" ? ` (${logins.github.host})` : ""}`
    : null
  const parts = [gh, cliWords("Claude", logins.claude), cliWords("Codex", logins.codex)].filter(
    (x): x is string => x !== null,
  )
  const line = parts.length > 0 ? parts.join(" · ") : "No logins found"
  return logins.known ? line : `${line} · update the runner to see all of them`
}

/**
 * The amber "personal logins" mark (spec §3.5): a Claude or Codex subscription (Max, Pro, Plus: a person's own
 * plan), or a GitHub login equal to the owner's own. A warning, not a block.
 */
export const personalLogins = (logins: RunnerLogins | null, ownerGithub?: string | null): boolean =>
  logins !== null &&
  (logins.claude?.kind === "subscription" ||
    logins.codex?.kind === "subscription" ||
    (ownerGithub != null && logins.github?.login.toLowerCase() === ownerGithub.toLowerCase()))

/** "Lea", "Lea and Eda", "Lea, Sol and Eda". */
export const andList = (names: ReadonlyArray<string>): string =>
  names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`

/**
 * A personal machine an agent thinks on (spec §7.5). Decision 0128: the owner's messages run on their own logins, a
 * teammate's only on a login the owner set Team. "Lea thinks here: your messages to it run here with your logins;
 * teammates' only with a login you share with the team"; for a teammate, "… with a login Jannik shares with the team".
 * Null when no agent thinks there.
 */
export const thinksHereLine = (
  agentNames: ReadonlyArray<string>,
  ownerIsViewer: boolean,
  ownerName: string | null,
): string | null => {
  if (agentNames.length === 0) return null
  const verb = agentNames.length === 1 ? "thinks" : "think"
  const them = agentNames.length === 1 ? "it" : "them"
  const head = `${andList(agentNames)} ${verb} here: your messages to ${them} run here`
  if (ownerIsViewer) return `${head} with your logins; teammates' only with a login you share with the team`
  return `${head} only with a login ${ownerName ?? "its owner"} shares with the team`
}

/** Someone's personal machine, for everyone else: "Just Becky's: only their work runs here, …" (the name once). */
export const justTheirsLine = (ownerName: string | null): string => {
  const who = ownerName ? (ownerName.trim().split(/\s+/)[0] ?? ownerName) : null
  return who
    ? `Just ${possessive(who)}: only their work runs here, what they lend, and the chats of agents assigned here.`
    : "Just its owner's: only their work runs here, what they lend, and the chats of agents assigned here."
}

/**
 * Fold one `WatchRunners` event into a runner list (the push carries the public view only; every other field of a
 * known row stays). A runner first seen on the push joins as `make(view)`.
 */
export const mergeLive = <R extends RunnerLike>(
  list: ReadonlyArray<R>,
  event: RunnerLiveEvent,
  make: (view: RunnerPublicView) => R | null,
): ReadonlyArray<R> => {
  if (event._tag === "RunnerRemoved") return list.filter((r) => r.runnerId !== event.runnerId)
  const v = event.runner
  const at = list.findIndex((r) => r.runnerId === v.runnerId)
  if (at < 0) {
    const row = make(v)
    return row ? [...list, row] : list
  }
  const prev = list[at] as R
  const next = {
    ...prev,
    name: v.name,
    os: v.os,
    arch: v.arch,
    ownerUserId: v.ownerUserId,
    status: v.status,
    lastSeenAt: v.lastSeenAt,
    serves: v.serves,
    version: v.version,
    ...(v.activeRuns !== undefined ? { activeRuns: v.activeRuns } : {}),
  } as R
  const out = [...list]
  out[at] = next
  return out
}

/**
 * The v1 runner list the project page holds (`RunnerListItemV1`) has no `serves` or `logins` yet: carry them over from
 * the H8 rows of the same endpoint, so `servesOf`, `runnerFactsOf` and `loginsOfRow` read them on either list. Rows
 * the H8 list doesn't have keep what they had.
 */
export const withSharing = <R extends { readonly runnerId: string }>(
  rows: ReadonlyArray<R>,
  full: ReadonlyArray<{
    readonly runnerId: string
    readonly serves?: RunnerServes | undefined
    readonly logins?: unknown
  }>,
): ReadonlyArray<R> =>
  full.length === 0
    ? rows
    : rows.map((r) => {
        const f = full.find((x) => x.runnerId === r.runnerId)
        if (!f) return r
        return {
          ...r,
          ...(f.serves !== undefined ? { serves: f.serves } : {}),
          ...(f.logins !== undefined ? { logins: f.logins } : {}),
        }
      })

/** The raw `logins` of any runner row (H8, or a v1 item `withSharing` filled). */
export const loginsOfRow = (r: object): unknown => (r as { readonly logins?: unknown }).logins

/**
 * Wave E (spec §7.5): the agents that think on each machine, from Home's project summaries: every active agent pinned to
 * a runner (`pinned`), else (a server from before E) each project's lead and where it thinks. `projectIds` keeps only
 * those projects' agents (the invite sheet: the projects the invitee joins).
 */
export const agentsThinkingOn = (
  home: HomeData | undefined,
  projectIds?: ReadonlyArray<string>,
): ReadonlyMap<string, ReadonlyArray<string>> => {
  const out = new Map<string, string[]>()
  const add = (pin: string, name: string) => {
    const names = out.get(pin) ?? []
    if (!names.includes(name)) names.push(name)
    out.set(pin, names)
  }
  for (const p of home?.projects ?? []) {
    if (projectIds && !projectIds.includes(p.id)) continue
    const pinned = p.live?.pinned
    if (pinned) {
      for (const a of pinned) add(a.runnerId, a.name)
      continue
    }
    const lead = p.live?.lead
    const pin = lead?.mind._tag === "runner" ? lead.mind.requires.runner : undefined
    if (lead && pin) add(pin, lead.name)
  }
  return out
}

/**
 * Wave E (spec §3.5, §5.3), owner only: what the last re-key left behind, or null when there is nothing to say. A nack
 * or a timeout keeps the old key ("Couldn't re-key vps-1: switch again"); an offline or too-old runner had its key
 * cleared and pairs again. The address of a "new address" reconnect is never shown, only that it happened.
 */
export const rekeyLines = (r: {
  readonly name: string
  readonly rekey?: unknown
  readonly newAddressAt?: unknown
}): {
  readonly rekey: { readonly tone: "busy" | "error"; readonly text: string } | null
  readonly newAddress: string | null
} => {
  const k = r.rekey as { readonly state?: unknown } | null | undefined
  const state = k && typeof k === "object" ? k.state : undefined
  const rekey =
    state === "pending"
      ? { tone: "busy" as const, text: `Giving ${r.name} a new key…` }
      : state === "failed"
        ? { tone: "error" as const, text: `Couldn't re-key ${r.name}: switch again.` }
        : state === "repair"
          ? { tone: "error" as const, text: `${r.name} couldn't take a new key while away: pair it again.` }
          : null
  const newAddress =
    typeof r.newAddressAt === "number" && Number.isFinite(r.newAddressAt)
      ? `${r.name} reconnected from a new address while another connection was live.`
      : null
  return { rekey, newAddress }
}

/** Did the switch's re-key go through (or is there no re-key state, a server from before E)? */
export const rekeyWentThrough = (r: { readonly rekey?: unknown }): boolean => {
  const k = r.rekey as { readonly state?: unknown } | null | undefined
  return !k || typeof k !== "object" || k.state === undefined || k.state === "done"
}
